// Delete / remove an FTO or COA request from its own page.
//
// Owner rule (2026-10-01):
//   • the employee can delete their own request only while nobody has
//     reviewed it (supervisor and HR both still pending);
//   • once a manager or HR has approved or denied it, the employee cannot;
//     HR or IT Admin remove it (the server reverses any balance deduction).
// The list page (index.js) applies the same rule through deleteRuleFor.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { deleteFto, deleteCoa, voidFtoByHr, voidCoaByHr, deleteRuleFor } from "../ftoService";

const DeleteRequestButton = ({ kind = "fto", id = 0, row, auth }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    if (!id) return null;

    const rule = deleteRuleFor(row, auth);
    const label = rule.mode === "hr" ? "Remove (HR / IT Admin)" : "Delete request";

    const run = async () => {
        try {
            setBusy(true);
            if (kind === "coa") {
                if (rule.mode === "hr") await voidCoaByHr(id); else await deleteCoa(id);
            } else {
                if (rule.mode === "hr") await voidFtoByHr(id); else await deleteFto(id);
            }
            toast.success("Request deleted.");
            setOpen(false);
            navigate("/attendance");
        } catch (e) {
            const msg = e?.response?.data?.message;
            toast.error(msg ? `Could not delete request: ${msg}` : "Could not delete request.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button
                color="danger" outline
                disabled={!rule.allowed}
                title={rule.why}
                onClick={() => setOpen(true)}
                className="me-auto"
            >
                <i className="ri-delete-bin-line me-1" />
                {label}
            </Button>
            <Modal isOpen={open} toggle={() => !busy && setOpen(false)} centered>
                <ModalHeader toggle={() => !busy && setOpen(false)}>Confirm Delete</ModalHeader>
                <ModalBody>
                    <h6 className="mb-1">Delete this request?</h6>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        This action cannot be undone. The request will be permanently removed.
                        {rule.mode === "hr" && " Removed by HR / IT Admin: any FTO balance deduction is reversed."}
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button color="danger" onClick={run} disabled={busy}>
                        {busy && <Spinner size="sm" className="me-1" />}
                        Yes, Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default DeleteRequestButton;
